import { calcularScore, PESOS } from '../../utils/seoScore';
import { calcularScore as notaDoCanal, PESOS as PESOS_DO_CANAL } from '../../utils/canalScore';

/**
 * A execução de exemplo que a página inicial mostra.
 *
 * O site é `loja.example.com`, um domínio reservado: nenhum site de verdade
 * aparece com defeitos inventados. Os totais por área não são digitados; saem
 * do detalhe de cada fragmento (status, descrições, tempos), e a nota sai de
 * `calcularScore`, a mesma função do painel e do relatório. Assim nenhum
 * número da página contradiz outro.
 *
 * shortcut: dados fixos de uma loja fictícia; trocar por uma execução real
 * quando houver uma que possa ser mostrada.
 */
const STATUS = { '2xx': 1231, '3xx': 24, '4xx': 22, '5xx': 7 };
const SEM_CANONICAL = 38;
const NAO_RENDERIZOU = 5;
const SEM_DESCRICAO = 286;
const SEM_TITULO = 19;
const SEM_H1 = 107;
/* Tempo de resposta do servidor: < 1 s, 1–2, 2–3, 3–5, > 5. */
const TEMPOS = [640, 286, 101, 198, 59];
const SEM_VIEWPORT = 48;
const COM_IMAGEM_SEM_ALT = 731;
const COM_LINK_QUEBRADO = 121;
const SEM_LINK_INTERNO = 7;

export const EXECUCAO = {
  site: 'loja.example.com',
  numero: 33,
  anterior: 71, // nota da execução #32, nas mesmas páginas
  data: '2026-10-22',
  total: Object.values(STATUS).reduce((a, b) => a + b, 0),
  sitemap: 1312,
  rastreadas: 796,
  fechadasPeloRobots: 14,
  status: STATUS,
  semCanonical: SEM_CANONICAL,
  naoRenderizou: NAO_RENDERIZOU,
  semDescricao: SEM_DESCRICAO,
  tempos: TEMPOS,
  lentas: TEMPOS[3] + TEMPOS[4],
  semViewport: SEM_VIEWPORT,
  linksQuebrados: COM_LINK_QUEBRADO,
  semLinkInterno: SEM_LINK_INTERNO,
  afetadas: {
    indexacao: STATUS['3xx'] + STATUS['4xx'] + STATUS['5xx'] + SEM_CANONICAL + NAO_RENDERIZOU,
    conteudo: SEM_DESCRICAO + SEM_TITULO + SEM_H1,
    experiencia: TEMPOS[3] + TEMPOS[4] + SEM_VIEWPORT,
    midia: COM_IMAGEM_SEM_ALT,
    links: COM_LINK_QUEBRADO + SEM_LINK_INTERNO
  }
};

/** Número com casas decimais fixas no idioma da tela: `decimalEm(locale)(9.6262)` → "9,63". */
export const decimalEm = (locale, casas = 2) => (n) =>
  Number(n).toLocaleString(locale, { minimumFractionDigits: casas, maximumFractionDigits: casas });

export const notaDe = (afetadas, total = EXECUCAO.total) => calcularScore({ total, ...afetadas });
export const perdaDe = (area, afetadas, total = EXECUCAO.total) => (PESOS[area] * afetadas) / total;
export const NOTA = notaDe(EXECUCAO.afetadas);

/* Do menor peso ao maior: as barras sobem para a direita, como as da marca. */
export const AREAS_EM_ORDEM = Object.keys(PESOS).sort((a, b) => PESOS[a] - PESOS[b]);
/* A ordem do painel: indexação primeiro, porque sem ela o resto não importa. */
export const AREAS_DO_PAINEL = ['indexacao', 'conteudo', 'experiencia', 'midia', 'links'];
export { PESOS };

export const ROTULO_DA_AREA = {
  indexacao: 'painelSite.abaIndexacao',
  conteudo: 'painelSite.abaConteudo',
  experiencia: 'painelSite.abaExperiencia',
  midia: 'painelSite.abaMidia',
  links: 'painelSite.abaLinks'
};

/* Linhas do registro do rastreador: status, caminho, segundos. */
export const REGISTRO = [
  [200, '/produtos/tenis-corrida-pro-x', 0.8],
  [200, '/categoria/lancamentos', 1.1],
  [301, '/promo → /promocao-verao', 0.2],
  [404, '/promocao-verao', 0.3],
  [200, '/blog/como-escolher-tenis', 3.4],
  [200, '/produtos/meia-compressao', 0.9],
  [404, '/trocas', 0.2],
  [200, '/categoria/trilha', 3.1],
  [200, '/institucional/sobre', 0.7],
  [500, '/busca?q=tenis', 5.2]
];

/* A planilha: os nomes de coluna são os campos que o rastreador grava. */
export const COLUNAS_DA_PLANILHA = [
  'url',
  'status',
  'title_length',
  'meta_description_length',
  'h1',
  'load_time_ms',
  'images_without_alt'
];
export const PLANILHA = [
  ['/produtos/tenis-corrida-pro-x', 200, 71, 0, 1, 812, 3],
  ['/categoria/lancamentos', 200, 48, 0, 1, 1104, 0],
  ['/promocao-verao', 404, 0, 0, 0, 288, 0],
  ['/blog/como-escolher-tenis', 200, 54, 142, 1, 3412, 0],
  ['/produtos/meia-compressao', 200, 39, 0, 0, 903, 2],
  ['/categoria/trilha', 200, 33, 131, 1, 3087, 0],
  ['/institucional/sobre', 200, 41, 156, 1, 688, 0],
  ['/busca?q=tenis', 500, 0, 0, 0, 5201, 0],
  ['/trocas', 404, 0, 0, 0, 201, 0],
  ['/blog/guia-de-tamanhos', 200, 46, 128, 1, 944, 1]
];

/* As correções que a análise de IA devolveria para esta execução, já na ordem dela. */
export const CORRECOES = [
  { chave: 'ia1', area: 'conteudo', paginas: SEM_DESCRICAO, impacto: 'Alto', esforco: 'Baixo' },
  { chave: 'ia2', area: 'links', paginas: COM_LINK_QUEBRADO, impacto: 'Alto', esforco: 'Baixo' },
  { chave: 'ia3', area: 'indexacao', paginas: SEM_CANONICAL, impacto: 'Alto', esforco: 'Alto' },
  { chave: 'ia4', area: 'experiencia', paginas: TEMPOS[3] + TEMPOS[4], impacto: 'Alto', esforco: 'Alto' },
  { chave: 'ia5', area: 'midia', paginas: COM_IMAGEM_SEM_ALT, impacto: 'Baixo', esforco: 'Baixo' }
];

/* Um canal do YouTube de exemplo, com a nota pela fórmula de `canalScore`. */
export const CANAL = {
  nome: '@canal.example',
  videos: 48,
  execucao: 7,
  afetados: { metadados: 11, engajamento: 9, desempenho: 14, publicacao: 3 }
};
export const NOTA_DO_CANAL = notaDoCanal({ total: CANAL.videos, ...CANAL.afetados });
export const AREAS_DO_CANAL = Object.keys(PESOS_DO_CANAL).sort((a, b) => PESOS_DO_CANAL[a] - PESOS_DO_CANAL[b]);
export { PESOS_DO_CANAL };
